import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useLocalStorageState from "use-local-storage-state";
import { useGetRequest } from "../../Utils/hooks/QueryHandler";
import { UpgradePlan } from "../../CommonComponents/UpgradePlan";
import { AuthorizationCard } from "../../CommonComponents/AuthorizationCard";
import { QuotationHeader } from "./QutotationModule/QuotationHeader";
// @ts-ignore
import { QuotationListing } from "./QutotationModule/QuotationListing";
import { useGetEssentials } from "../../Utils/hooks/useGetEssentials";
import { useGlobalState } from "../../Utils/hooks/useFilterQueryValue";
import { fetchTemporaryFilterData } from "../../Utils/helpers/commonhelper";
import useCommonData from "../../Utils/actions/advanceFilteringAction";
import { GetRequest } from "../../Utils/hooks/ApiHandler";

export const Quotations = () => {

  const { customer, setCustomer, headers, extras, navigate, customerId, loginUserId, userType } = useGetEssentials()
  const quotationMetadata = JSON.parse(localStorage.getItem("quotationMetadata"))
  // rbac permission object 
  const [PERMISSION, _] = useLocalStorageState("permission");

  const [isMyApproval, setMyApproval] = useState(() => {
    return quotationMetadata?.isMyApproval || false
  });

  const [allQuotation, setQuotation] = useState([]);
  // States for using pagination
  const [limit, setLimit] = useState(25);

  const [allOffset, setAllOffset] = useState(0);
  const [myApprovalOffset, setMyApprovalOffset] = useState(0);

  const [search, setSearch] = useState("");
  // active pagination based on tab
  const activeOffset = isMyApproval ? myApprovalOffset : allOffset;
  const setActiveOffset = isMyApproval ? setMyApprovalOffset : setAllOffset;

  const [total_count, setTotalCount] = useState(0)

  // states to store the filter data
  const [filterStates, setFilterState] = useState([]);

  // checking and validating if the last selected filter still exist until then the state initialized would be false and no data or the heading would be renderd
  const [isInitialized, setIsInitialized] = useState(false);

  const [heading, setHeading] = useState("All Quotations");

  // loading state for temporary filter data to be hydrated
  const [tempFilterDataLoading, setTempFilterDataLoading] = useState(false)

  const [quotationFilterValues, setQuoationFilterValues] = useGlobalState("quotationApplyFilterQueryValues")

  const [quotationFilterDataParams, setQuotationFilterDataParams] = useGlobalState(
    "quotationFilterDataParams",
    { currAdvFilter: null, applyFilterData: {} },
  )

  // curr advance filter to store the current filter type between "apply" and "save as" in advance filter for respective pagination in apply filter
  const [currAdvFilter, setCurrAdvFilter] = useState(
    () => quotationFilterDataParams?.currAdvFilter || null,
  )

  // state for filter dropdown of quotations
  const [filterDropdown, setFilterDropdown] = useState(false);
  // filter state to see the temoporary applied filter data,
  const [tempFilterData, setTempFilterData] = useState(null);

  // state to store deals data
  const [deals, setDeals] = useState([])
  // filter categories to render within the heading dropdown
  const [filterCategories, setFilterCategories] = useState([]);
  // state to store the advanced custom filter to fetch data on save
  const [customFilter, setCustomFilter] = useState(null)

  // permission if subscription plan encorporates this module
  const isQuotation = PERMISSION.quotation?.isAuthorized
  // const quotationPermission 
  const quotationGrant = PERMISSION?.quotation

  useEffect(() => {
    if (!isInitialized) {
      const lastHeading = quotationMetadata?.filterType?.filterName;
      setHeading(lastHeading || "All Quotations");
      setIsInitialized(true);
    }
  }, [isInitialized, quotationMetadata]);

  // common advance filter api calls that will be needed everywhere
  const { leadOwner, company, contacts } = useCommonData({
    leadOwner: true,
    leadSource: false,
    companies: true,
    contacts: true
  })

  // filter dropdown options
  const dropdownOptions = [
    { value: "reference_number", label: "Reference Number" },
    { value: "contact_id", label: "Contact", option: contacts },
    { value: "deal_id", label: "Deal Name", option: deals },
    { value: "owner_id", label: "Owner", option: leadOwner },
    { value: "quotation_number", label: "Quotation Number" },
    { value: "company_id", label: "Company Name", option: company },
    { value: "quotation_date", label: "Quotation Date", type: "date-range" },
    { value: "expiry_date", label: "Expiry Date", type: "date-range" },
    { value: "created_at", label: "Created at", type: "date-range" },
  ];


  const params = {
    limit,
    offset: activeOffset,
    search,
  };

  // New function to fetch deals
  const fetchDeals = async () => {
    const res = await GetRequest(
      `common/fetchAllDeals/${customerId}/${loginUserId}`,
      { headers },
      { customer, setCustomer, rf: headers.Authentication, navigate }
    );
    if (!res) return;
    if (res.success === false) return;

    setDeals(
      res.data?.map((c) => ({
        label: c.deal_name + " " + `(${c.deal_no})`,
        value: c.id,
      }))
    );
  };

  useEffect(() => {
    fetchDeals()
  }, [])

  const applyFilterDataParams = {
    filterValues: quotationFilterValues,
    currView: "list",
    offset: activeOffset,
    limit: limit,
    module_name: "quotation",
    customerId: customerId,
    loginUserId: loginUserId,
    userType: userType,
    headers: headers,
    extras: extras,
    setTempFilterData: setTempFilterData,
    setLoading: setTempFilterDataLoading,
    setFilterState: setFilterState,
    setFilterDropdown: setFilterDropdown,
  }

  useEffect(() => {
    setQuotationFilterDataParams({
      currAdvFilter: currAdvFilter,
      applyFilterData: {
        ...applyFilterDataParams,
        filterValues: quotationFilterValues,
        currView: "list",
        offset: activeOffset,
        limit: limit,
      },
    })
  }, [currAdvFilter, quotationFilterValues, activeOffset, limit, isMyApproval])

  useEffect(() => {
    if (
      currAdvFilter === "apply" &&
      quotationFilterValues?.["filter_values"]?.length > 0 &&
      !isMyApproval
    ) {
      fetchTemporaryFilterData(quotationFilterDataParams?.applyFilterData)
    }
  }, [quotationFilterDataParams?.applyFilterData, isMyApproval])

  const { endpoint, queryKey } = useMemo(() => {
    if (currAdvFilter === "apply" && !isMyApproval) {
      return {
        endpoint: "",
        queryKey: ""
      }
    }

    // resetting state if current advance filter is null
    setQuoationFilterValues({ filter_state: [], filter_values: [] })

    const customFilterId = quotationMetadata?.filterType?.id
    const lowerHeading = heading
      ? heading?.toLowerCase()?.split(" ").join("_")
      : heading;

    if (isMyApproval) {
      return {
        endpoint: `quotation/fetchMyApprovals/${customerId}/${loginUserId}`,
        queryKey: "getApprovals"
      }
    }
    if (!customFilter && !customFilterId) {
      console.log('fetching one of the endpoint')
      if (lowerHeading === "all_quotations") {
        return {
          endpoint: `quotation/fetchQuotation/${customerId}/${loginUserId}/${userType}`,
          queryKey: "fetchAllQuotations"
        }
      }
    }
    if (customFilter || customFilterId) {
      const id = customFilter?.id || customFilterId
      return {
        endpoint: `quotation/fetchQuotation/${customerId}/${loginUserId}/${userType}/${id}`,
        queryKey:
          `filterQuery${id}`
      }
    }
    return {
      endpoint: "",
      queryKey: ""
    }
  }, [heading, filterCategories, isMyApproval, customFilter, currAdvFilter]);

  // Single useGetRequest call
  const {
    data: quotationData,
    refetch: fetchQuotation,
    isLoading
  } = useGetRequest(
    endpoint,
    [queryKey, isMyApproval],
    headers,
    extras,
    params
  );

  // storing all contacts to a state to use it in the custom dropdown for the dynamic fields
  useEffect(() => {
    if (currAdvFilter === 'apply') return

    if (
      quotationData &&
      quotationData.filter_data &&
      quotationData.filter_data[0] &&
      quotationData.filter_data[0].filter_values
    ) {
      const values = quotationData.filter_data[0].filter_values;
      const parsedState = JSON.parse(values);
      const filteredArray = parsedState?.map((state) => {
        const dropdownValue = dropdownOptions.find(
          (opt) => opt.value === state.label_name
        );

        return {
          id: state.label_name,
          field: state.label_name,
          label_name: dropdownValue ? dropdownValue.label : state.label_name,
          option: dropdownValue.option,
          subFilterType: state.label_query,
          value:
            state.label_query === "between"
              ? { start: state.label_value[0], end: state.label_value[1] }
              : state.label_value,
        };
      });
      setFilterState(filteredArray);
    } else {
      setFilterState([]);
    }

    setQuotation(quotationData?.data);
    setTotalCount(quotationData?.["total_count"])
  }, [quotationData, isMyApproval, currAdvFilter]);

  // useEffect(() => {
  //   fetchQuotation();
  // }, [isMyApproval, endpoint]);

  const columnOptions = [
    // { key: "sn", label: "#",locked: true },
    { key: "quotation_number", label: "Quotation Number", mandatory: true },
    { key: "contact_full_name", label: "Contact" },
    { key: "phone", label: "Contact No" },
    { key: "company_name", label: "Company" },
    { key: "quotation_date", label: "Quotation Date" },
    { key: "expiry_date", label: "Expiry Date" },
    { key: "owner_full_name", label: "Lead Owner" },
    { key: "deal_name", label: "Deal" },
    { key: "quotation_status", label: "Quotation Status" },
  ];

  // Initialize columns for the table - ALL columns visible by default
  const [columns, setColumns] = useState(() => {
    const saved = quotationMetadata?.visibleColumns;
    const allColumnKeys = columnOptions?.map(col => col.label);
    if (saved) {
      try {
        return saved;
      } catch (e) {
        console.error("Error parsing visible columns:", e);
      }
    }
    return allColumnKeys;
  });

  // Save view preference to localStorage
  useEffect(() => {
    localStorage.setItem("quotationMetadata", JSON.stringify(
      {
        ...quotationMetadata,
        isMyApproval: isMyApproval,
      }
    ))
  }, [isMyApproval])

  // if plan doesn't include quotation then show upgrade plan modal
  if (!isQuotation) {
    return <UpgradePlan moduleName="Quotations" />
  }

  // if view permission isn't given by the admin or the POC then show authorization card.
  if (!quotationGrant?.isView) {
    return (
      <div style={{ position: "absolute", left: "40%", top: "10rem" }}>
        <AuthorizationCard />
      </div>
    );
  }

  return (
    <>

      <QuotationHeader
        heading={heading}
        setHeading={setHeading}
        // quotationGrant={quotationGrant}
        isMyApproval={isMyApproval}
        setMyApproval={setMyApproval}
        columnOptions={columnOptions}
        setActiveOffset={setActiveOffset}
        filterCategories={filterCategories}
        setFilterCategories={setFilterCategories}
        columns={columns}
        setColumns={setColumns}
        filterStates={filterStates}
        setFilterState={setFilterState}
        setTempFilterData={setTempFilterData}
        setCurrAdvFilter={setCurrAdvFilter}
        setSearch={setSearch}
        setCustomFilter={setCustomFilter}
        refetchQuotation={fetchQuotation}
        quotationMetadata={quotationMetadata}
        dropdownOptions={dropdownOptions}
        filterDropdown={filterDropdown}
        setFilterDropdown={setFilterDropdown}
        filterValues={quotationFilterValues?.filter_state}
        setQuoationFilterValues={setQuoationFilterValues}
      />
      <QuotationListing
        tempFilterData={tempFilterData}
        allQuotation={allQuotation}
        limit={limit}
        setLimit={setLimit}
        columns={columns}
        activeOffset={activeOffset}
        setActiveOffset={setActiveOffset}
        quotationData={quotationData}
        fetchQuotation={fetchQuotation}
        // quotationGrant={quotationGrant}
        isLoading={isLoading}
        isMyApproval={isMyApproval}
        total_count={total_count}
      />
    </>
  );
};